import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Search, Edit2, Trash2, Loader2, CloudUpload, CheckCircle, Clock, Video, FileText, Music, Link as LinkIcon, X } from 'lucide-react';
import { db } from '../firebase/config';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import defaultCourses from '../constants/defaultCourses.json';

const CourseRegistry = () => {
    const [activeTab, setActiveTab] = useState('courses'); // 'courses' or 'masterClasses'
    const [courses, setCourses] = useState([]);
    const [masterClasses, setMasterClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSeeding, setIsSeeding] = useState(false);

    // Modal state for Master Class
    const [showClassModal, setShowClassModal] = useState(false);
    const [editingClass, setEditingClass] = useState(null);
    const [classForm, setClassForm] = useState({
        title: '', description: '', instructor: '', tags: '', type: 'video', youtubeId: '', driveUrl: '', coverUrl: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [coursesSnap, masterClassesSnap] = await Promise.all([
                getDocs(collection(db, 'courses')),
                getDocs(collection(db, 'masterClasses'))
            ]);
            setCourses(coursesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setMasterClasses(masterClassesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSeedCourses = async () => {
        if (!window.confirm(`Are you sure you want to seed ${defaultCourses.length} default courses?`)) return;
        setIsSeeding(true);
        try {
            for (const course of defaultCourses) {
                await setDoc(doc(db, 'courses', course.id), { ...course, addedAt: new Date().toISOString() });
            }
            alert("Successfully seeded courses to Firestore!");
            fetchData();
        } catch (error) {
            console.error("Error seeding courses:", error);
            alert("Failed to seed courses. Check console or Firestore permissions.");
        } finally {
            setIsSeeding(false);
        }
    };

    const handleSaveMasterClass = async (e) => {
        e.preventDefault();
        try {
            const id = editingClass?.id || `mc_${Date.now()}`;
            const payload = {
                ...classForm,
                tags: classForm.tags.split(',').map(t => t.trim()).filter(Boolean),
                updatedAt: new Date().toISOString()
            };
            if (!editingClass) payload.createdAt = new Date().toISOString();

            await setDoc(doc(db, 'masterClasses', id), payload);
            alert("Saved successfully!");
            setShowClassModal(false);
            fetchData();
        } catch (error) {
            console.error("Error saving master class:", error);
            alert("Failed to save.");
        }
    };

    const handleDeleteMasterClass = async (id) => {
        if (!window.confirm("Are you sure you want to delete this master class?")) return;
        try {
            await deleteDoc(doc(db, 'masterClasses', id));
            fetchData();
        } catch (error) {
            console.error("Error deleting class:", error);
        }
    };

    const filteredCourses = courses.filter(c => c.title?.toLowerCase().includes(searchQuery.toLowerCase()) || c.category?.toLowerCase().includes(searchQuery.toLowerCase()));
    const filteredMasterClasses = masterClasses.filter(c => c.title?.toLowerCase().includes(searchQuery.toLowerCase()) || c.instructor?.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
        <div className="p-8 max-w-[1400px] mx-auto h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                        <BookOpen className="text-[#8B1A2D]" size={28} />
                        Course Registry
                    </h1>
                    <p className="text-gray-500 mt-1">Manage all programs and standalone master classes.</p>
                </div>
                <div className="flex gap-4">
                    {activeTab === 'courses' ? (
                        <>
                            <button onClick={handleSeedCourses} disabled={isSeeding} className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-bold text-sm disabled:opacity-50">
                                {isSeeding ? <Loader2 size={16} className="animate-spin" /> : <CloudUpload size={16} />}
                                Seed from Default
                            </button>
                            <button className="flex items-center gap-2 px-4 py-2 bg-[#8B1A2D] text-white rounded-xl hover:bg-[#6b1422] transition-all font-bold text-sm shadow-lg shadow-[#8B1A2D]/20">
                                <Plus size={16} /> Add New Course
                            </button>
                        </>
                    ) : (
                        <button 
                            onClick={() => {
                                setEditingClass(null);
                                setClassForm({ title: '', description: '', instructor: '', tags: '', type: 'video', youtubeId: '', driveUrl: '', coverUrl: '' });
                                setShowClassModal(true);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-[#8B1A2D] text-white rounded-xl hover:bg-[#6b1422] transition-all font-bold text-sm shadow-lg shadow-[#8B1A2D]/20"
                        >
                            <Plus size={16} /> Add Master Class
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 mb-6 border-b border-gray-200">
                <button 
                    onClick={() => setActiveTab('courses')}
                    className={`px-4 py-3 font-bold text-sm border-b-2 transition-colors ${activeTab === 'courses' ? 'border-[#8B1A2D] text-[#8B1A2D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    SUNY Courses
                </button>
                <button 
                    onClick={() => setActiveTab('masterClasses')}
                    className={`px-4 py-3 font-bold text-sm border-b-2 transition-colors ${activeTab === 'masterClasses' ? 'border-[#8B1A2D] text-[#8B1A2D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    Master Classes & Content
                </button>
            </div>

            {/* Metrics & Search */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <BookOpen size={24} />
                    </div>
                    <div>
                        <div className="text-sm font-bold text-gray-500">Total {activeTab === 'courses' ? 'Registered' : 'Master Classes'}</div>
                        <div className="text-2xl font-black text-gray-900">{activeTab === 'courses' ? courses.length : masterClasses.length}</div>
                    </div>
                </div>

                <div className="md:col-span-2 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input 
                        type="text" 
                        placeholder={`Search ${activeTab === 'courses' ? 'courses by name or category' : 'master classes by name or instructor'}...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full h-full pl-12 pr-4 bg-white border border-gray-200 rounded-2xl outline-none focus:border-[#8B1A2D] focus:ring-4 focus:ring-[#8B1A2D]/10 transition-all font-medium text-gray-900"
                    />
                </div>
            </div>

            {/* Data Table */}
            <div className="flex-1 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
                <div className="overflow-x-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="p-4 text-xs font-black text-gray-500 uppercase tracking-wider">Title</th>
                                {activeTab === 'courses' ? (
                                    <>
                                        <th className="p-4 text-xs font-black text-gray-500 uppercase tracking-wider">Category</th>
                                        <th className="p-4 text-xs font-black text-gray-500 uppercase tracking-wider text-center">Modules</th>
                                        <th className="p-4 text-xs font-black text-gray-500 uppercase tracking-wider text-center">Weekly Target</th>
                                    </>
                                ) : (
                                    <>
                                        <th className="p-4 text-xs font-black text-gray-500 uppercase tracking-wider">Instructor</th>
                                        <th className="p-4 text-xs font-black text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="p-4 text-xs font-black text-gray-500 uppercase tracking-wider">Tags</th>
                                    </>
                                )}
                                <th className="p-4 text-xs font-black text-gray-500 uppercase tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="p-12 text-center text-gray-500">
                                        <Loader2 className="animate-spin mx-auto mb-2 text-[#8B1A2D]" size={32} />
                                        Loading registry...
                                    </td>
                                </tr>
                            ) : activeTab === 'courses' ? (
                                filteredCourses.length === 0 ? (
                                    <tr><td colSpan="5" className="p-12 text-center text-gray-500 font-medium">No courses found.</td></tr>
                                ) : (
                                    filteredCourses.map(course => (
                                        <tr key={course.id} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="p-4">
                                                <div className="font-bold text-gray-900">{course.title}</div>
                                                <div className="text-xs font-semibold text-gray-500 mt-0.5 font-mono">{course.id}</div>
                                            </td>
                                            <td className="p-4">
                                                <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg text-[10px] font-black uppercase tracking-wider">{course.category}</span>
                                            </td>
                                            <td className="p-4 text-center"><span className="font-black text-gray-700">{course.totalModules || 12}</span></td>
                                            <td className="p-4 text-center">
                                                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-lg border border-green-100">
                                                    <Clock size={12} className="shrink-0" />
                                                    <span className="font-black text-xs">{course.weeklyCourseHoursExpectation} hrs</span>
                                                </div>
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button className="p-2 text-gray-400 hover:text-[#8B1A2D] hover:bg-red-50 rounded-lg transition-colors"><Edit2 size={16} /></button>
                                                    <button className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )
                            ) : (
                                filteredMasterClasses.length === 0 ? (
                                    <tr><td colSpan="5" className="p-12 text-center text-gray-500 font-medium">No master classes found.</td></tr>
                                ) : (
                                    filteredMasterClasses.map(cls => (
                                        <tr key={cls.id} className="hover:bg-gray-50/50 transition-colors group">
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    {cls.coverUrl ? (
                                                        <img src={cls.coverUrl} alt="" className="w-10 h-10 rounded-lg object-cover bg-gray-200" />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                                                            <BookOpen size={16} />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="font-bold text-gray-900">{cls.title}</div>
                                                        <div className="text-xs font-semibold text-gray-500 mt-0.5">{cls.id}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4 text-sm font-medium text-gray-700">{cls.instructor}</td>
                                            <td className="p-4">
                                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-bold border border-purple-100">
                                                    {cls.type === 'video' ? <Video size={12} /> : cls.type === 'audio' ? <Music size={12} /> : <FileText size={12} />}
                                                    <span className="capitalize">{cls.type}</span>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {(cls.tags || []).slice(0,3).map(t => (
                                                        <span key={t} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-bold">{t}</span>
                                                    ))}
                                                    {cls.tags?.length > 3 && <span className="px-2 py-0.5 text-gray-400 text-[10px] font-bold">+{cls.tags.length - 3}</span>}
                                                </div>
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button 
                                                        onClick={() => {
                                                            setEditingClass(cls);
                                                            setClassForm({ ...cls, tags: cls.tags?.join(', ') || '' });
                                                            setShowClassModal(true);
                                                        }}
                                                        className="p-2 text-gray-400 hover:text-[#8B1A2D] hover:bg-red-50 rounded-lg transition-colors"
                                                    ><Edit2 size={16} /></button>
                                                    <button onClick={() => handleDeleteMasterClass(cls.id)} className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal for Master Class */}
            {showClassModal && (
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h2 className="text-lg font-black text-gray-900">{editingClass ? 'Edit Master Class' : 'Add New Master Class'}</h2>
                            <button onClick={() => setShowClassModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
                        </div>
                        <form onSubmit={handleSaveMasterClass} className="p-6 overflow-y-auto flex-1 flex flex-col gap-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Title</label>
                                    <input required type="text" value={classForm.title} onChange={e => setClassForm({...classForm, title: e.target.value})} className="w-full border border-gray-200 rounded-xl p-2.5 text-sm font-medium focus:border-[#8B1A2D] outline-none" placeholder="e.g. Intro to Cinematography" />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Instructor</label>
                                    <input required type="text" value={classForm.instructor} onChange={e => setClassForm({...classForm, instructor: e.target.value})} className="w-full border border-gray-200 rounded-xl p-2.5 text-sm font-medium focus:border-[#8B1A2D] outline-none" placeholder="Instructor Name" />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Description</label>
                                    <textarea rows={3} value={classForm.description} onChange={e => setClassForm({...classForm, description: e.target.value})} className="w-full border border-gray-200 rounded-xl p-2.5 text-sm font-medium focus:border-[#8B1A2D] outline-none resize-none" placeholder="Brief description..."></textarea>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Content Type</label>
                                    <select value={classForm.type} onChange={e => setClassForm({...classForm, type: e.target.value})} className="w-full border border-gray-200 rounded-xl p-2.5 text-sm font-medium focus:border-[#8B1A2D] outline-none bg-white">
                                        <option value="video">Video</option>
                                        <option value="audio">Audio</option>
                                        <option value="publication">Publication</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Tags (comma separated)</label>
                                    <input type="text" value={classForm.tags} onChange={e => setClassForm({...classForm, tags: e.target.value})} className="w-full border border-gray-200 rounded-xl p-2.5 text-sm font-medium focus:border-[#8B1A2D] outline-none" placeholder="Directing, Theory, etc." />
                                </div>
                                
                                {classForm.type === 'video' || classForm.type === 'audio' ? (
                                    <div className="col-span-2">
                                        <label className="block text-xs font-bold text-gray-600 mb-1">YouTube Video ID</label>
                                        <div className="relative">
                                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                            <input type="text" value={classForm.youtubeId} onChange={e => setClassForm({...classForm, youtubeId: e.target.value})} className="w-full pl-9 border border-gray-200 rounded-xl p-2.5 text-sm font-medium focus:border-[#8B1A2D] outline-none" placeholder="e.g. dQw4w9WgXcQ" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="col-span-2">
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Google Drive URL (for Publications)</label>
                                        <div className="relative">
                                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                            <input type="text" value={classForm.driveUrl} onChange={e => setClassForm({...classForm, driveUrl: e.target.value})} className="w-full pl-9 border border-gray-200 rounded-xl p-2.5 text-sm font-medium focus:border-[#8B1A2D] outline-none" placeholder="https://drive.google.com/file/d/..." />
                                        </div>
                                    </div>
                                )}
                                
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Cover Image URL</label>
                                    <input type="text" value={classForm.coverUrl} onChange={e => setClassForm({...classForm, coverUrl: e.target.value})} className="w-full border border-gray-200 rounded-xl p-2.5 text-sm font-medium focus:border-[#8B1A2D] outline-none" placeholder="https://..." />
                                </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end gap-3">
                                <button type="button" onClick={() => setShowClassModal(false)} className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-100 transition-colors">Cancel</button>
                                <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-bold bg-[#8B1A2D] text-white hover:bg-[#6b1422] transition-colors shadow-md">Save Class</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CourseRegistry;
